import { halloCommaSalutation, renderVoiceCopy, type Voice } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useUpdateVoiceSettings, useVoiceSettings } from '@/platform/voice/use-voice-settings';
import { voiceFrom } from '@/platform/voice/use-voice';

export function VoiceAdminPage() {
  usePageTitle('Anrede');
  const me = useMe();
  const settings = useVoiceSettings(Boolean(me.data));
  const update = useUpdateVoiceSettings();
  const loaded = settings.data ?? voiceFrom(me.data);

  return (
    <section className="w-full min-w-0 space-y-6">
      <PlatformPageHeader
        title="Anrede"
        description="Wie dieser Mandant Personen anspricht und Namen zeigt."
      />
      {settings.isLoading ? (
        <p>Anrede wird geladen …</p>
      ) : settings.isError ? (
        <p>Die Anrede konnte nicht geladen werden.</p>
      ) : (
        <VoiceForm
          key={`${loaded.addressForm}:${loaded.personNameParts}:${loaded.personNameOrder}`}
          initial={loaded}
          busy={update.isPending}
          actor={me.data}
          onSave={async (voice) => {
            try {
              await update.mutateAsync(voice);
              toast.success('Anrede gespeichert.');
            } catch (error) {
              toast.error(
                error instanceof ApiRequestError
                  ? error.message
                  : 'Die Anrede konnte nicht gespeichert werden.',
              );
            }
          }}
        />
      )}
    </section>
  );
}

function VoiceForm({
  initial,
  busy,
  actor,
  onSave,
}: {
  initial: Voice;
  busy: boolean;
  actor: { firstName: string; lastName: string; displayName: string } | null | undefined;
  onSave: (voice: Voice) => Promise<void>;
}) {
  const [addressForm, setAddressForm] = useState(initial.addressForm);
  const [personNameParts, setPersonNameParts] = useState(initial.personNameParts);
  const [personNameOrder, setPersonNameOrder] = useState(initial.personNameOrder);
  const voice: Voice = { addressForm, personNameParts, personNameOrder };
  const previewActor = actor ?? {
    firstName: 'Max',
    lastName: 'Mustermann',
    displayName: 'Max Mustermann',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Anrede und Namen</CardTitle>
        <CardDescription>
          Die Vorschau nutzt den angemeldeten Namen. Speichern schreibt die Einstellung.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="voice-address">Anrede</Label>
          <Select
            value={addressForm}
            onValueChange={(value) => setAddressForm(value as Voice['addressForm'])}
          >
            <SelectTrigger id="voice-address">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sie">Sie</SelectItem>
              <SelectItem value="du">Du</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="voice-parts">Name</Label>
          <Select
            value={personNameParts}
            onValueChange={(value) => setPersonNameParts(value as Voice['personNameParts'])}
          >
            <SelectTrigger id="voice-parts">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="given">Nur Vorname</SelectItem>
              <SelectItem value="given_family">Vor- und Nachname</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="voice-order">Reihenfolge</Label>
          <Select
            value={personNameOrder}
            onValueChange={(value) => setPersonNameOrder(value as Voice['personNameOrder'])}
            disabled={personNameParts === 'given'}
          >
            <SelectTrigger id="voice-order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="given_family">Vorname Nachname</SelectItem>
              <SelectItem value="family_given">Nachname, Vorname</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm">
          {halloCommaSalutation(previewActor, voice)}{' '}
          {renderVoiceCopy('voice.preview.sample', voice)}
        </p>
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            void onSave(voice);
          }}
        >
          Speichern
        </Button>
      </CardContent>
    </Card>
  );
}
